/**
 * Проекты (прежний `index.html.j2`): один проект — один рилс. Товар
 * подтягивается из CRM по mpProductID; обложка — последняя генерация проекта.
 *
 * Рядом с «Новый проект» — «Новый видео-ролик», два входа в ролик: из платья «Наших фото» (платье,
 * стилевая библиотека, цель → сразу вкладка «Сценарий») и из готового кадра «Результатов» (выбрал
 * картинку — проект создан, сразу вкладка «Кадры», ракурсы правятся там).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Film, Plus, Shirt } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { apiFetch } from "@/lib/api";
import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import { FramePickerDialog } from "@/components/results/FramePickerDialog";
import { keyedKey } from "@/lib/idempotencyKey";
import { libraryKeys, loadLibraries } from "@/lib/library";
import { mediaUrl, num, when } from "@/lib/media";
import { loadPhotos, photoKeys } from "@/lib/photos";
import type { ProjectRow } from "@/lib/project";
import { errorText, postJson, qk } from "@/lib/project";
import { makeVideoFromVariant } from "@/lib/results";
import { useCreateVideoProject, useVideoOptions } from "@/lib/video";
import { Btn, Empty, ErrorLine, Hint, inputClass, PageState, Panel, selectClass } from "@/components/project/ui";

export function ProjectsPage() {
  const query = useQuery({ queryKey: qk.projects, queryFn: () => apiFetch<ProjectRow[]>("/api/projects") });

  return (
    <div className="grid gap-4 p-5">
      <div>
        <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg">Проекты</h1>
        <p className="mt-1 text-[12.5px] text-fg-muted">
          Один проект — один рилс. Товар подтягивается из CRM по mpProductID.
        </p>
      </div>

      {/* Создание — НАД списком (задача Оли №319): с десятком проектов панели уезжали за экран,
          хотя завести проект — главное действие этой страницы. */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <NewProject />
        <NewVideoProject />
      </div>

      {query.data ? (
        <Panel annot="список проектов">
          {query.data.length === 0 ? (
            <Empty>Проектов пока нет.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-border text-[11.5px] text-fg-faint">
                    <th className="w-[52px] py-2 pr-3 font-normal" />
                    <th className="py-2 pr-3 font-normal">Проект</th>
                    <th className="py-2 pr-3 font-normal">Товар</th>
                    <th className="py-2 pr-3 text-right font-normal">Фото</th>
                    <th className="py-2 pr-3 text-right font-normal">Битые</th>
                    <th className="py-2 pr-3 text-right font-normal">Кадры</th>
                    <th className="py-2 font-normal">Изменён</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((p) => (
                    <tr
                      key={p.id}
                      data-annot={`проект ${p.id}`}
                      className="border-b border-border last:border-b-0 hover:bg-bg-hover"
                    >
                      <td className="py-1.5 pr-3">
                        <Link to={`/project/${p.id}`}>
                          {p.cover ? (
                            <img
                              src={mediaUrl(p.cover)}
                              alt=""
                              loading="lazy"
                              className="h-[56px] w-[32px] rounded-[3px] border border-border object-cover"
                            />
                          ) : (
                            <div className="grid h-[56px] w-[32px] place-items-center rounded-[3px] border border-dashed border-border-strong text-fg-faint">
                              <Film size={13} />
                            </div>
                          )}
                        </Link>
                      </td>
                      <td className="py-1.5 pr-3">
                        <Link to={`/project/${p.id}`} className="font-medium text-fg hover:text-accent-text">
                          {p.project_duration_ms != null ? (
                            <Clapperboard
                              size={13}
                              className="mr-1.5 inline-block align-[-2px] text-accent-text"
                              aria-label="видео-ролик"
                            />
                          ) : null}
                          {p.project_name}
                        </Link>
                      </td>
                      <td className="tnum py-1.5 pr-3 text-fg-muted">{p.mp_product_id ?? "—"}</td>
                      <td className="tnum py-1.5 pr-3 text-right">{num(p.assets_ok)}</td>
                      <td className="tnum py-1.5 pr-3 text-right">
                        {p.assets_broken ? (
                          <span className="rounded-full bg-err-bg px-2 py-0.5 text-[11.5px] text-err">
                            {num(p.assets_broken)}
                          </span>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="tnum py-1.5 pr-3 text-right">{num(p.shots)}</td>
                      <td className="tnum py-1.5 text-fg-muted">{when(p.date_modify)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : (
        <PageState loading={query.isPending} error={query.error ? errorText(query.error) : null} />
      )}

    </div>
  );
}

function NewProject() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [product, setProduct] = useState("");

  const create = useMutation({
    mutationFn: () =>
      postJson<{ id: number }>("/api/projects", {
        name: name.trim(),
        product: product.trim() ? Number(product) : null,
      }),
    onSuccess: async ({ id }) => {
      await client.invalidateQueries({ queryKey: qk.projects });
      void navigate(`/project/${id}`);
    },
  });

  const productBad = product.trim() !== "" && !/^\d+$/.test(product.trim());

  return (
    <Panel title="Новый проект" annot="новый проект">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !productBad && !create.isPending) create.mutate();
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="название, например «5331 · вечерняя лестница»"
          required
          className={`${inputClass} min-w-[280px] flex-1`}
        />
        <input
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          inputMode="numeric"
          placeholder="mpProductID"
          className={`${inputClass} tnum w-[140px]`}
        />
        <Btn type="submit" tone="primary" disabled={!name.trim() || productBad || create.isPending}>
          <Plus size={13} />
          Создать
        </Btn>
      </form>
      <Hint className="mt-1.5">Номер товара CRM нужен для импорта фото и фактов; без него проект создастся пустым.</Hint>
      <ErrorLine
        text={productBad ? "Номер товара — только цифры" : create.isError ? errorText(create.error) : ""}
        className="mt-2"
      />
    </Panel>
  );
}

const DEFAULT_SECONDS = 15;
const SECONDS_RANGE = [5, 60] as const; // `BAD_DURATION` вне 5000..60000 мс

/**
 * Новый видео-ролик, два входа.
 *
 * Из платья: платье (окно выбора по картинкам, берётся первое отмеченное), имя «Платье N · ролик»,
 * стилевая библиотека (умолчание — `default_library_id` из `/api/video/options`), цель в секундах.
 * Кадров проект не получает — их даст применённый сценарий.
 *
 * Из готового кадра (задачи Оли №304/№305): картинка «Результатов» (`FramePickerDialog`) — и сразу
 * проект, без окна параметров. Умолчания даёт СЕРВЕР (нескрытые ракурсы `generate.visible_angles`, кусок
 * `SHOT_MS_DEFAULT`, потолок `VIDEO_COST_CAP_DEFAULT`): свои значения отсюда не посылаем, чтобы их
 * было одно место. Правятся они там, где и у проекта из платья: состав и задание ракурсов — панелью
 * «Ракурсы», модель, длительность и потолок — в «Прогнать на моделях» на странице проекта.
 */
function NewVideoProject() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [picker, setPicker] = useState(false);
  const [dressId, setDressId] = useState<number | null>(null);
  const [framePicker, setFramePicker] = useState(false);
  const [name, setName] = useState("");
  const [libraryId, setLibraryId] = useState<number | null>(null);
  const [secondsText, setSecondsText] = useState(String(DEFAULT_SECONDS));

  // Список папок нужен окну и подписи выбранного платья — не раньше первого открытия окна.
  const dresses = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, enabled: picker || dressId !== null });
  const libraries = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries, enabled: dressId !== null });
  const options = useVideoOptions();
  const create = useCreateVideoProject();

  const dress = dresses.data?.dresses.find((d) => d.id === dressId) ?? null;
  const groups = libraries.data?.groups ?? [];
  const known = (id: number) => groups.some((g) => g.libs.some((l) => l.id === id));
  const fallback = options.data && known(options.data.default_library_id) ? options.data.default_library_id : null;
  const library = libraryId ?? fallback;

  const secs = Number(secondsText.replace(",", "."));
  const secsBad = !Number.isFinite(secs) || secs < SECONDS_RANGE[0] || secs > SECONDS_RANGE[1];

  const pick = (ids: number[]) => {
    const first = ids[0];
    if (first === undefined) return;
    const card = dresses.data?.dresses.find((d) => d.id === first);
    setDressId(first);
    setName(card?.name ? `${card.name} · ролик` : `Платье ${first} · ролик`);
    create.reset();
  };

  const submit = () => {
    if (dressId === null || secsBad || create.isPending) return;
    create.mutate(
      { name: name.trim(), dress_id: dressId, library_id: library, duration_ms: Math.round(secs * 1000) },
      { onSuccess: ({ id }) => void navigate(`/project/${id}?tab=scenario`) },
    );
  };

  // Ролик из готового кадра: выбрали картинку — проект уже создан и мы на его странице.
  // Ключ идемпотентности — НА ВАРИАНТ, не один на всю панель: сервер (`_frame_project_by_key`) ищет
  // проект ТОЛЬКО по ключу, вариант не сверяет, и общий ключ после обрыва связи (клиент увидел ошибку,
  // POST всё же создал проект) увёл бы следующий выбор ДРУГОЙ картинки в проект по предыдущей. Запись
  // в карте заводится при первом запуске для этого варианта и живёт до успеха — повтор (в т. ч. после
  // 409 `BUSY`) уходит с тем же ключом, а другой вариант получает свой. `inFlight` — тот же приём, что у
  // `useMakeVideo`: `isPending` в снимке рендера отстаёт на рендер, и без рефа клик успевал бы уйти дважды.
  const frameKeys = useRef(new Map<number, string>());
  const frameInFlight = useRef(false);
  const fromFrame = useMutation({
    mutationFn: (variantId: number) => makeVideoFromVariant(variantId, { key: keyedKey(frameKeys.current, variantId) }),
    onSuccess: async ({ project_id }, variantId) => {
      frameKeys.current.delete(variantId);
      await client.invalidateQueries({ queryKey: qk.projects });
      void navigate(`/project/${project_id}?tab=shots`);
    },
    onSettled: () => {
      frameInFlight.current = false;
    },
  });
  const pickFrame = (variantId: number) => {
    setFramePicker(false);
    if (frameInFlight.current) return;
    frameInFlight.current = true;
    fromFrame.mutate(variantId);
  };

  return (
    <Panel title="Новый видео-ролик" annot="новый видео-ролик">
      <div className="flex flex-wrap items-center gap-2">
        <Btn onClick={() => setPicker(true)}>
          <Shirt size={13} />
          {dressId === null ? "Выбрать платье" : "Другое платье"}
        </Btn>
        <Btn onClick={() => setFramePicker(true)} disabled={fromFrame.isPending}>
          <Clapperboard size={13} />
          {fromFrame.isPending ? "Создаю ролик…" : "Из готового кадра"}
        </Btn>
        {dressId !== null ? (
          <span className="flex items-center gap-2 text-[12.5px] text-fg">
            {dress?.cover?.thumb_path ? (
              <img
                src={mediaUrl(dress.cover.thumb_path)}
                alt=""
                className="h-[42px] w-[28px] rounded-[3px] border border-border object-cover"
              />
            ) : null}
            <Link to={`/photos/${dressId}`} className="hover:text-accent-text">
              {dress?.name ?? `Платье #${dressId}`}
            </Link>
            {dress ? <span className="text-fg-faint">· фото: {num(dress.count)}</span> : null}
          </span>
        ) : null}
      </div>

      {dressId !== null ? (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="название ролика"
            aria-label="название ролика"
            className={`${inputClass} w-full`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={library ?? ""}
              onChange={(e) => setLibraryId(e.target.value ? Number(e.target.value) : null)}
              aria-label="стилевая библиотека"
              className={`${selectClass} min-w-[220px] flex-1`}
            >
              {library === null ? <option value="">стилевая библиотека…</option> : null}
              {groups.map((g) => (
                <optgroup key={g.code} label={g.name}>
                  {g.libs.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} · {num(l.photos)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
              цель
              <input
                value={secondsText}
                onChange={(e) => setSecondsText(e.target.value)}
                inputMode="decimal"
                aria-label="цель ролика, секунд"
                className={`${inputClass} tnum w-[64px] text-right`}
              />
              с
            </label>
            <Btn type="submit" tone="primary" disabled={secsBad || create.isPending}>
              <Plus size={13} />
              Создать
            </Btn>
          </div>
        </form>
      ) : null}

      <Hint className="mt-1.5">
        Фото платья встанут в слоты проекта; кадры даст сценарий — после создания откроется вкладка «Сценарий».
        «Из готового кадра» — ролик из картинки «Результатов»: проект заведётся сразу, кадрами станут она и все
        ракурсы; состав ракурсов и их задания правятся на странице проекта, там же запускается генерация.
      </Hint>
      <ErrorLine
        text={
          fromFrame.isError
            ? errorText(fromFrame.error)
            : secsBad && dressId !== null
              ? `Цель ролика — от ${SECONDS_RANGE[0]} до ${SECONDS_RANGE[1]} с`
              : create.isError
                ? errorText(create.error)
                : libraries.isError
                  ? `Библиотеки не загрузились: ${errorText(libraries.error)}`
                  : dresses.isError
                    ? `Платья не загрузились: ${errorText(dresses.error)}`
                    : ""
        }
        className="mt-2"
      />

      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={dresses.data}
        taken={NO_IDS}
        annotate={NO_NOTES}
        triedAll={null}
        onAdd={pick}
      />

      <FramePickerDialog open={framePicker} onClose={() => setFramePicker(false)} onPick={pickFrame} />
    </Panel>
  );
}

const NO_IDS: readonly number[] = [];
const NO_NOTES: ReadonlyMap<number, string> = new Map();
